import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useListCleaningAreasQuery } from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import CleaningSheet from './CleaningSheet';
import CleaningRecords from './CleaningRecords';

type View = 'sheet' | 'records';

/**
 * Cleaning Schedule. One room-grouped sheet for everyone; the admin-only
 * Records view (month chart, print, export) is added by CleaningRecords.
 */
const CleaningPage: React.FC = () => {
  // Resolve locationId from any route shape the page is registered under:
  //   /cleaning (flat) → useLocationContext.activeLocationId
  //   /locations/:locationId/cleaning → params.locationId
  //   /loc/:locId/cleaning → params.locId
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const { isAdmin } = useAuth();
  const [view, setView] = useState<View>('sheet');

  const {
    data: areasData,
    isLoading,
    isError,
    refetch,
  } = useListCleaningAreasQuery({ locationId: locationId! }, { skip: !locationId });

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Failed to load cleaning areas.</p>
        <button
          type="button"
          data-testid="cleaning-retry"
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const areas = areasData?.areas ?? [];

  return (
    <div data-testid="cleaning-page" className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex flex-wrap items-center gap-3 cleaning-print-hide">
        <h1 className="text-xl font-semibold text-gray-900">Cleaning Schedule</h1>
        {/* Records (month chart, print, export) is an oversight surface:
            admin-only, like cleaning:read-export on the backend. */}
        {isAdmin && (
          <div
            role="tablist"
            className="ml-auto inline-flex rounded-md border border-gray-300 overflow-hidden text-sm"
          >
            {(['sheet', 'records'] as View[]).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                data-testid={`cleaning-view-${v}`}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 ${
                  view === v ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {v === 'sheet' ? 'Sheet' : 'Records'}
              </button>
            ))}
          </div>
        )}
      </div>
      {view === 'sheet' ? (
        <CleaningSheet locationId={locationId} isAdmin={isAdmin} areas={areas} />
      ) : (
        <CleaningRecords locationId={locationId} areas={areas} />
      )}
    </div>
  );
};

export default CleaningPage;
